import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, type ReactNode } from 'react';
import {
  createExperienceEventsQueryKey,
  createGetMagicChatQueryKey,
  createGetMagicJobQueryKey,
} from '@/utils/queryKeys';
import { useDeleteEvent } from '../../hooks/useDeleteEvent';
import { useRemoveEvent } from '../../hooks/useRemoveEvent';
import { useUpdateEvent } from '../../hooks/useUpdateEvent';
import type { UpdateEventMutation } from '../../hooks/useUpdateEvent.generated';
import { baseToFor } from '../../lib/base.lib';
import type { CollectionBase, DecoratedEvent } from '../../types';
import { CollectionContext } from './CollectionContext';

type DeleteOptions = NonNullable<Parameters<typeof useDeleteEvent>[1]>;
type RemoveOptions = NonNullable<Parameters<typeof useRemoveEvent>[1]>;

type Props = {
  children: ReactNode;
  base: CollectionBase | null;
  setBase?: (base: CollectionBase | null) => void;
  events?: DecoratedEvent[];
  onUpdate?: (result: UpdateEventMutation['updateEvent']) => void;
  onDelete?: DeleteOptions['onSuccess'];
  onRemove?: RemoveOptions['onSuccess'];
};

const noop = () => void 0;

export const CollectionContextProvider = ({
  children,
  base,
  setBase = noop,
  events: providedEvents,
  onUpdate,
  onDelete,
  onRemove,
}: Props) => {
  const queryClient = useQueryClient();
  const eventFor = useMemo(() => baseToFor(base), [base]);

  const revalidate = useCallback(() => {
    const { chat, job, experience } = base ?? {};

    if (job?.id) {
      queryClient.invalidateQueries({
        queryKey: createGetMagicJobQueryKey(job.id),
      });
    }
    if (chat?.id) {
      queryClient.invalidateQueries({
        queryKey: createGetMagicChatQueryKey(chat.id),
      });
    }
    if (experience?.id) {
      queryClient.invalidateQueries({
        queryKey: createExperienceEventsQueryKey(experience.id),
      });
    }
    if (experience?.publishedSlug) {
      queryClient.invalidateQueries({
        queryKey: createExperienceEventsQueryKey(experience.publishedSlug),
      });
    }
  }, [queryClient, base]);

  const deleteMutation = useDeleteEvent(eventFor, {
    onSuccess: (data, variables, onMutateResult, context) => {
      revalidate();
      onDelete?.(data, variables, onMutateResult, context);
    },
  });

  const updateMutation = useUpdateEvent(eventFor, {
    onSuccess: (data) => {
      revalidate();
      onUpdate?.(data.updateEvent);
    },
  });

  const removeMutation = useRemoveEvent(
    { experienceId: base?.experience?.id ?? 'REMOVE_ONLY_SUPPORTS_EXPERIENCE' },
    {
      onSuccess: (data, variables, onMutateResult, context) => {
        revalidate();
        onRemove?.(data, variables, onMutateResult, context);
      },
    }
  );

  const events =
    providedEvents ?? base?.chat?.events ?? base?.job?.events ?? [];

  return (
    <CollectionContext.Provider
      value={{
        base,
        setBase,
        events,
        deleteMutation,
        updateMutation,
        removeMutation,
        revalidate,
      }}
    >
      {children}
    </CollectionContext.Provider>
  );
};
